import { _ as __makeTemplateObject, f as __read, d as __assign, u as useCogniteContext, b as __awaiter, e as __generator } from '../../chunks/chunk-525a49bc.js';
import 'antd';
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import '../../chunks/chunk-53391a7d.js';
import { L as LoadingBlock } from '../../chunks/chunk-c2b06a8e.js';
import { groupBy } from 'lodash';
import { event } from 'd3';
import { select } from 'd3-selection';
import { zoom } from 'd3-zoom';
import { scaleTime } from 'd3-scale';

var EventTimelineType;
(function (EventTimelineType) {
    EventTimelineType["discrete"] = "discrete";
    EventTimelineType["continuous"] = "continuous";
})(EventTimelineType || (EventTimelineType = {}));
var EventTimelineView;
(function (EventTimelineView) {
    EventTimelineView["fill"] = "fill";
    EventTimelineView["outline"] = "outline";
})(EventTimelineView || (EventTimelineView = {}));

var Event = function (props) {
    var scale = props.scale, start = props.start, end = props.end, _a = props.type, type = _a === void 0 ? EventTimelineType.continuous : _a, _b = props.view, view = _b === void 0 ? EventTimelineView.fill : _b, _c = props.discreteWidth, discreteWidth = _c === void 0 ? 2 : _c, _d = props.height, height = _d === void 0 ? 10 : _d, color = props.color;
    var x = scale(start);
    var y = -height / 2;
    var width = end && type !== EventTimelineType.discrete ? scale(end) - x : discreteWidth;
    var opacity = view === EventTimelineView.fill || type === EventTimelineType.discrete
        ? 1
        : 0.5;
    return (React.createElement(Group, null,
        opacity !== 1 ? (React.createElement("rect", { width: discreteWidth, height: height, fill: color, y: y, x: x })) : null,
        React.createElement("rect", { width: width, height: height, fill: color, y: y, x: x, opacity: opacity })));
};
var Group = styled('g')(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  cursor: pointer;\n"], ["\n  cursor: pointer;\n"])));
var templateObject_1;

var Ruler = function (props) {
    var width = props.width, height = props.height, positionChanged = props.positionChanged;
    var _a = __read(useState(null), 2), x = _a[0], setX = _a[1];
    var mouseMoveHandler = function (e) {
        var offsetX = e.nativeEvent.offsetX;
        setX(offsetX);
        if (positionChanged) {
            positionChanged(e);
        }
    };
    var hideRuler = function () {
        setX(null);
        if (positionChanged) {
            positionChanged(null);
        }
    };
    return (React.createElement("g", { "data-test-id": "ruler" },
        x !== null && (React.createElement("path", { style: { pointerEvents: 'none' }, d: "M0,0V" + height, stroke: '#ccc', strokeWidth: 1, transform: "translate(" + x + ", 0)" })),
        React.createElement("rect", { style: { pointerEvents: 'all' }, x: 0, y: 0, width: width, height: height, fill: 'none', onMouseMove: mouseMoveHandler, onMouseLeave: hideRuler })));
};

// Copyright 2020 Cognite AS
var Timeline = function (props) {
    var events = props.events, width = props.width, color = props.color, height = props.height, index = props.index, _a = props.padding, padding = _a === void 0 ? 10 : _a, scale = props.scale;
    var renderEvents = function () {
        return events.map(function (_a) {
            var s = _a.startTime, e = _a.endTime, id = _a.id, _b = _a.appearance, view = _b.view, type = _b.type;
            return (React.createElement(Event, { key: id, color: color, start: s, end: e, scale: scale, view: view, type: type, height: height }));
        });
    };
    return (React.createElement("g", { transform: "translate(0, " + (index * (height + padding) + height) + ")" },
        React.createElement("path", { d: "M0,0H" + width, stroke: color, strokeWidth: 2 }),
        renderEvents()));
};

var getScaleTime = function (width, start, end) {
    return scaleTime()
        .domain([start, end])
        .range([0, width]);
};
var getEventsByTimestamp = function (timestamp, timelines) {
    return Object.keys(timelines).flatMap(function (color) {
        var tl = timelines[color];
        return tl.reduce(function (filtered, event) {
            var startTime = event.startTime, endTime = event.endTime;
            if (startTime === timestamp ||
                (startTime && endTime && startTime <= timestamp && endTime >= timestamp)) {
                filtered.push(__assign(__assign({}, event), { color: color }));
            }
            return filtered;
        }, []);
    });
};

var ChartLayout = function (props) {
    var svg = props.svg, width = props.width, height = props.height, timelines = props.timelines, tlHeight = props.timelineSize.height, start = props.start, end = props.end, _a = props.ruler, _b = _a === void 0 ? {
        show: false,
        onChange: undefined,
        onHide: undefined,
    } : _a, showRuler = _b.show, onRulerChange = _b.onChange, onRulerHide = _b.onHide, _c = props.zoom, _d = _c === void 0 ? {
        enable: false,
        onZoomStart: undefined,
        onZoom: undefined,
        onZoomEnd: undefined,
    } : _c, zoomEnable = _d.enable, onZoomStart = _d.onZoomStart, onZoom = _d.onZoom, onZoomEnd = _d.onZoomEnd;
    var _e = __read(useState(showRuler), 2), rulerVisibility = _e[0], setRulerVisibility = _e[1];
    var scale = getScaleTime(width, start, end);
    var handleZoom = function () {
        if (!zoomEnable || !svg || !svg.current) {
            return null;
        }
        var zoom$1 = zoom()
            .scaleExtent([1, 100])
            .on('start', zoomStart)
            .on('zoom', zoomed)
            .on('end', zoomEnd);
        select(svg.current).call(zoom$1);
        return zoom$1;
    };
    var removeZoomListener = function (zoom) {
        if (zoom) {
            zoom
                .on('start', null)
                .on('end', null)
                .on('zoom', null);
        }
    };
    var zoomStart = function () {
        setRulerVisibility(false);
        if (onZoomStart) {
            onZoomStart();
        }
    };
    var zoomed = function () {
        if (!event || event.type !== 'zoom') {
            return;
        }
        var t = event.transform;
        var newDomain = t.rescaleX(scale).domain().map(function (date) { return date.getTime(); });
        if (onZoom) {
            onZoom(newDomain);
        }
    };
    var zoomEnd = function () {
        setRulerVisibility(showRuler);
        if (onZoomEnd) {
            onZoomEnd();
        }
    };
    var onRulerMove = function (event) {
        if (!event) {
            if (onRulerHide) {
                onRulerHide();
            }
            return;
        }
        var offsetX = event.nativeEvent.offsetX;
        var time = scale.invert(offsetX).getTime();
        if (onRulerChange) {
            var filteredEvents = getEventsByTimestamp(time, timelines);
            onRulerChange({ event: event, timestamp: time, timelineEvents: filteredEvents });
        }
    };
    useEffect(function () {
        var zoom = handleZoom();
        return function () {
            removeZoomListener(zoom);
        };
    }, [svg]);
    var renderTimelines = function () {
        return Object.keys(timelines).map(function (color, index) { return (React.createElement(Timeline, { key: color, events: timelines[color], width: width, color: color, height: tlHeight, index: index, scale: scale })); });
    };
    return (React.createElement(React.Fragment, null,
        renderTimelines(),
        rulerVisibility && (React.createElement(Ruler, { width: width, height: height, positionChanged: onRulerMove }))));
};

var toTimelinesDefault = function (_) { return '#000'; };
var minimalWidth = 100;
var EventsTimeline = function (_a) {
    var events = _a.events, start = _a.start, end = _a.end, ruler = _a.ruler, zoom = _a.zoom, toTimelines = _a.toTimelines, _b = _a.timelineSize, _c = _b === void 0 ? {
        height: 10,
        bottom: 10,
    } : _b, tlHeight = _c.height, tlBottom = _c.bottom, dateFormatter = _a.dateFormatter;
    var context = useCogniteContext(EventsTimeline);
    var svgRef = useRef(null);
    var wrapperRef = useRef(null);
    var _d = __read(useState(0), 2), width = _d[0], setWidth = _d[1];
    var _e = __read(useState(0), 2), height = _e[0], setHeight = _e[1];
    var _f = __read(useState([start, end]), 2), domain = _f[0], setDomain = _f[1];
    var _g = __read(useState({}), 2), timelines = _g[0], setTimelines = _g[1];
    var handleResize = function () {
        if (!wrapperRef.current) {
            return;
        }
        var currentWidth = wrapperRef.current.getBoundingClientRect().width;
        setWidth(currentWidth || minimalWidth);
    };
    var retrieveEvents = function () { return __awaiter(void 0, void 0, void 0, function () {
        var ids, sdkEvents;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    ids = events.map(function (_a) {
                        var id = _a.id;
                        return ({ id: id });
                    });
                    return [4 /*yield*/, context.events.retrieve(ids)];
                case 1:
                    sdkEvents = _a.sent();
                    return [2 /*return*/, sdkEvents.map(function (e, i) {
                            var _a = events[i], view = _a.view, type = _a.type;
                            return __assign(__assign({}, e), { appearance: { view: view, type: type } });
                        })];
            }
        });
    }); };
    var onEventsPropChanges = function () { return __awaiter(void 0, void 0, void 0, function () {
        var timelineEvents, groupTimelines, componentHeight;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, retrieveEvents()];
                case 1:
                    timelineEvents = _a.sent();
                    groupTimelines = groupBy(timelineEvents, toTimelines || toTimelinesDefault);
                    componentHeight = Object.keys(groupTimelines).length * (tlHeight + tlBottom);
                    setTimelines(groupTimelines);
                    setHeight(componentHeight);
                    return [2 /*return*/];
            }
        });
    }); };
    var onZoomCallback = function (newDomain) {
        setDomain(newDomain);
        if (zoom.onZoom) {
            zoom.onZoom(newDomain);
        }
    };
    var renderDates = function () {
        if (!dateFormatter) {
            return null;
        }
        var dates = domain.map(function (date) { return (React.createElement("span", { key: date }, dateFormatter(date))); });
        return React.createElement(Dates, { "data-test-id": "dates" }, dates);
    };
    useEffect(function () {
        handleResize();
        window.addEventListener('resize', handleResize);
        return function () { return window.removeEventListener('resize', handleResize); };
    }, []);
    useEffect(function () {
        onEventsPropChanges();
    }, [events]);
    var zoomConfig = zoom ? __assign(__assign({}, zoom), { onZoom: onZoomCallback }) : zoom;
    return (React.createElement(Wrapper, { ref: wrapperRef },
        renderDates(),
        width && Object.keys(timelines).length ? (React.createElement("svg", { ref: svgRef, width: width, height: height },
            React.createElement(ChartLayout, { svg: svgRef, width: width, height: height, timelines: timelines, start: domain[0], end: domain[1], ruler: ruler, zoom: zoomConfig, timelineSize: { height: tlHeight, bottom: tlBottom } }))) : (React.createElement(LoadingBlock, { height: '50px' }))));
};
var Wrapper = styled.div(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  width: 100%;\n  min-height: 1px;\n"], ["\n  width: 100%;\n  min-height: 1px;\n"])));
var Dates = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  width: 100%;\n  justify-content: space-between;\n"], ["\n  display: flex;\n  width: 100%;\n  justify-content: space-between;\n"])));
var templateObject_1$1, templateObject_2;

export { EventTimelineType, EventTimelineView, EventsTimeline };
